import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { type ReactNode, useState } from 'react';

const TRANSITION = '0.25s cubic-bezier(0.4, 0, 0.2, 1)';

interface CollapsiblePanelProps {
  title: string;
  titleIcon?: ReactNode;
  count?: number;
  expandedWidth?: number;
  collapsedWidth?: number;
  children: ReactNode;
  collapsedContent?: ReactNode;
  footer?: ReactNode;
  collapsedFooter?: ReactNode;
  defaultCollapsed?: boolean;
  alignSelf?: 'flex-start' | 'stretch';
}

export const CollapsiblePanel = ({
  title,
  titleIcon,
  count,
  expandedWidth = 250,
  collapsedWidth = 52,
  children,
  collapsedContent,
  footer,
  collapsedFooter,
  defaultCollapsed = false,
  alignSelf = 'stretch',
}: CollapsiblePanelProps) => {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <Box
      sx={{
        width: collapsed ? collapsedWidth : expandedWidth,
        minWidth: collapsed ? collapsedWidth : expandedWidth,
        display: 'flex',
        flexDirection: 'column',
        alignSelf,
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        border: `1px solid ${alpha(theme.border.color, 0.7)}`,
        boxShadow: `0 1px 4px ${alpha('#000', 0.03)}, 0 4px 12px ${alpha('#000', 0.02)}`,
        overflow: 'hidden',
        transition: `width ${TRANSITION}, min-width ${TRANSITION}, box-shadow ${TRANSITION}`,
        '&:hover': {
          boxShadow: `0 2px 8px ${alpha('#000', 0.05)}, 0 4px 16px ${alpha('#000', 0.03)}`,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          px: collapsed ? 0.5 : 1.5,
          py: 1.25,
          borderBottom: `1px solid ${alpha(theme.border.color, 0.5)}`,
          minHeight: 46,
          background: `linear-gradient(180deg, ${alpha(theme.table.stripeBg, 0.5)} 0%, transparent 100%)`,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, overflow: 'hidden' }}>
            {titleIcon}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: '0.8125rem',
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                color: theme.palette.text.primary,
              }}
            >
              {title}
            </Typography>
            {count != null && (
              <Box
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  borderRadius: '10px',
                  px: 0.75,
                  py: 0.125,
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  lineHeight: 1.4,
                  minWidth: 18,
                  textAlign: 'center',
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        )}
        <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="right" arrow>
          <IconButton
            size="small"
            onClick={() => setCollapsed((prev) => !prev)}
            sx={{
              width: 28,
              height: 28,
              color: theme.palette.text.secondary,
              borderRadius: '8px',
              transition: `all 0.2s ease`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      <Box
        sx={{ flex: 1, overflow: 'auto', cursor: collapsed ? 'pointer' : 'default' }}
        onClick={collapsed ? () => setCollapsed(false) : undefined}
      >
        {collapsed ? collapsedContent : children}
      </Box>

      {/* Footer */}
      {(collapsed ? collapsedFooter : footer) && (
        <Box sx={{ borderTop: `1px solid ${alpha(theme.border.color, 0.5)}` }}>
          {collapsed ? collapsedFooter : footer}
        </Box>
      )}
    </Box>
  );
};
